/**
 * Knowledge Base Detail Page - Phase 10
 * 
 * Displays detailed information for a single Knowledge Base entry (rule).
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api, KBEntryDetail } from '../lib/api'
import { LoadingSpinner } from '../components/design-system/Loading'
import { Badge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { useLocale } from '../contexts/LocaleContext'
import { formatLabel, severityLabel, tagLabel } from '../lib/i18n'

export function KnowledgeBaseDetailPage() {
  const { t, locale } = useLocale()
  const { entryId } = useParams<{ entryId: string }>()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [entry, setEntry] = useState<KBEntryDetail | null>(null)
  
  useEffect(() => {
    if (entryId) {
      loadEntry()
    }
  }, [entryId])
  
  async function loadEntry() {
    try {
      setLoading(true)
      setError(null)
      const data = await api.getKBEntry(entryId!)
      setEntry(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('kb.entryFailed'))
    } finally {
      setLoading(false)
    }
  }
  
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <LoadingSpinner size="lg" text={t('kb.entryLoading')} />
      </div>
    )
  }
  
  if (error) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px' }}>
        <div
          style={{
            border: '1px solid var(--reviewer-danger-border)',
            backgroundColor: 'var(--reviewer-danger-soft)',
            padding: '24px',
          }}
        >
          <h2 style={{ 
            margin: 0, 
            marginBottom: '12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-lg)',
            color: 'var(--reviewer-danger)',
          }}>
            {t('kb.entryFailed')}
          </h2>
          <p style={{ 
            margin: 0, 
            marginBottom: '16px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text)',
          }}>
            {error}
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button onClick={loadEntry} variant="danger">
              {t('common.retry')}
            </Button>
            <Button onClick={() => navigate("/knowledge-base")} variant="secondary">
              {t('kb.back')}
            </Button>
          </div>
        </div>
      </div>
    )
  }
  
  if (!entry) {
    return null
  }
  
  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div
        style={{
          marginBottom: '24px',
          fontFamily: 'var(--reviewer-font-mono)',
          fontSize: 'var(--reviewer-text-sm)',
          color: 'var(--reviewer-text-muted)',
        }}
      >
        <Link to="/knowledge-base" style={{ color: 'var(--reviewer-primary)', textDecoration: 'none' }}>
          {t('nav.knowledgeBase')}
        </Link>
        {' / '}
        <span>{entry.rule_id}</span>
      </div>
      
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1
          style={{
            margin: 0,
            marginBottom: '12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-2xl)',
            fontWeight: 600,
            color: 'var(--reviewer-text)',
          }}
        >
          {entry.title}
        </h1>
        
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Badge variant={entry.severity === 'critical' || entry.severity === 'high' ? 'danger' : 'warning'}>
            {severityLabel(locale, entry.severity)}
          </Badge>
          <Badge variant="default">{t(`kb.cat.${entry.category}`)}</Badge>
          <Badge variant="default">{formatLabel(locale, entry.format_type)}</Badge>
          <Badge variant="default">{t('kb.confidence')}: {entry.confidence}</Badge>
          <Badge variant="default">v{entry.version}</Badge>
        </div>
        
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '8px 16px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text-muted)',
          }}
        >
          <span>{t('kb.ruleId', { id: entry.rule_id })}</span>
          <span />
          <span>{t('kb.source')}</span>
          <span style={{ color: 'var(--reviewer-text)' }}>{entry.provenance_source}</span>
        </div>
      </div>
      
      {/* Description */}
      <section style={{ marginBottom: '32px' }}>
        <h2
          style={{
            margin: 0,
            marginBottom: '12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-lg)',
            fontWeight: 600,
            color: 'var(--reviewer-text)',
          }}
        >
          {t('kb.description')}
        </h2>
        <div
          style={{
            padding: '16px',
            border: '1px solid var(--reviewer-border)',
            backgroundColor: 'var(--reviewer-surface)',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text)',
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
          }}
        >
          {entry.description}
        </div>
      </section>
      
      {/* Impact */}
      {entry.impact && (
        <section style={{ marginBottom: '32px' }}>
          <h2
            style={{
              margin: 0,
              marginBottom: '12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-lg)',
              fontWeight: 600,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('kb.impact')}
          </h2>
          <div
            style={{
              padding: '16px',
              border: '1px solid var(--reviewer-warning-border)',
              backgroundColor: 'var(--reviewer-warning-soft)',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              color: 'var(--reviewer-text)',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6',
            }}
          >
            {entry.impact}
          </div>
        </section>
      )}
      
      {/* Remediation */}
      {entry.remediation && (
        <section style={{ marginBottom: '32px' }}>
          <h2
            style={{
              margin: 0,
              marginBottom: '12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-lg)',
              fontWeight: 600,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('kb.remediation')}
          </h2>
          <div
            style={{
              padding: '16px',
              border: '1px solid var(--reviewer-success-border)',
              backgroundColor: 'var(--reviewer-success-soft)',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              color: 'var(--reviewer-text)',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6',
            }}
          >
            {entry.remediation}
          </div>
        </section>
      )}
      
      {/* CWE IDs */}
      {entry.cwe_ids && entry.cwe_ids.length > 0 && (
        <section style={{ marginBottom: '32px' }}>
          <h2
            style={{
              margin: 0,
              marginBottom: '12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-lg)',
              fontWeight: 600,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('kb.cwe')}
          </h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {entry.cwe_ids.map((cwe) => (
              <a
                key={cwe}
                href={`https://cwe.mitre.org/data/definitions/${cwe.replace('CWE-', '')}.html`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--reviewer-border)',
                  backgroundColor: 'var(--reviewer-surface)',
                  fontFamily: 'var(--reviewer-font-mono)',
                  fontSize: 'var(--reviewer-text-sm)',
                  color: 'var(--reviewer-primary)',
                  textDecoration: 'none',
                }}
              >
                {cwe}
              </a>
            ))}
          </div>
        </section>
      )}
      
      {/* References */}
      {entry.references && entry.references.length > 0 && (
        <section style={{ marginBottom: '32px' }}>
          <h2
            style={{
              margin: 0,
              marginBottom: '12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-lg)',
              fontWeight: 600,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('kb.references')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {entry.references.map((ref, idx) => (
              <a
                key={idx}
                href={ref}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '12px',
                  border: '1px solid var(--reviewer-border)',
                  backgroundColor: 'var(--reviewer-surface)',
                  fontFamily: 'var(--reviewer-font-mono)',
                  fontSize: 'var(--reviewer-text-sm)',
                  color: 'var(--reviewer-primary)',
                  textDecoration: 'none',
                  wordBreak: 'break-all',
                }}
              >
                {ref}
              </a>
            ))}
          </div>
        </section>
      )}
      
      {/* Tags */}
      {entry.tags && entry.tags.length > 0 && (
        <section style={{ marginBottom: '32px' }}>
          <h2
            style={{
              margin: 0,
              marginBottom: '12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-lg)',
              fontWeight: 600,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('kb.tagsHeading')}
          </h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {entry.tags.map((tag) => (
              <Badge key={tag} variant="default">
                {tagLabel(locale, tag)}
              </Badge>
            ))}
          </div>
        </section>
      )}
      
      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <Button onClick={() => navigate("/knowledge-base")} variant="secondary">
          {t('kb.back')}
        </Button>
      </div>
    </div>
  )
}
